'use client';

import React, { useEffect, useState } from 'react';
import AdminChatProvider, { useAdminChat } from './admin';
import CustomerChatProvider, { useCustomerChat } from './customer';
import { adminSupabase } from '../../client/adminSupabaseClient';
import { customerSupabase } from '../../client/customerSupabaseClient';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import NewMessageToast from '../../components/NewMessageToast';

const ChatProvider = ({ children }) => {
    return (
        <AdminChatProvider>
            <CustomerChatProvider>
                {children}
            </CustomerChatProvider>
        </AdminChatProvider>
    );
};

export const useChat = (receiver) => {
    if (!receiver || !['admin', 'seller', 'customer'].includes(receiver)) {
        throw new Error('Invalid receiver value. Use admin or customer.');
    }

    const { role, sellerId: sellerIdInt, userInfo: authUser } = useSelector((state) => state.auth);
    const sellerId = sellerIdInt?.toString();

    const adminChat = useAdminChat();
    const customerChat = useCustomerChat();

    const chat = receiver === 'customer' ? customerChat : adminChat;
    const supabase = receiver === 'admin' ? adminSupabase : customerSupabase;

    const [subscriptions, setSubscriptions] = useState([]);

    //helper methods
    const handleOnGetNewMessage = async (roomId, newMessage) => {
        chat.appendMessages("end", roomId, [newMessage]);
        const senderId = sellerId ? sellerId.toString() : authUser.id.toString();
        if (Number(newMessage.sender_id) !== Number(senderId)) {
            toast.custom((t) => (
                <NewMessageToast
                    t={t}
                    id={newMessage.sender_id}
                    name={`New Message from ${receiver}`}
                    content={newMessage.media_type === 'text' ? newMessage.content.toString() : 'Send an image.'}
                    type={receiver}
                />
            ));
        }
    };

    const handleOnChatRoomChange = async (chatRoom) => {
        try {
            const { data, error } = await supabase.from('messages').select('*').eq('room_id', chatRoom.id);
            const messages = error ? [] : data;
            const chatRoomsWithData = await chat.loadChatRoomData([{ ...chatRoom, messages }]);
            chat.insertOrUpdateLocalChatRoom(chatRoomsWithData[0]);
        } catch (error) {
            console.log('error', error);
        }
    };

    useEffect(() => {
        if (sellerId || authUser) {
            const subscribedChannels = [];

            const chatRoomSubscription = supabase
                .channel('public:chat_rooms')
                .on(
                    'postgres_changes',
                    {
                        event: 'INSERT',
                        schema: 'public',
                        table: 'chat_rooms',
                        ...(receiver !== 'seller' && { filter: `seller_id=in.(${sellerId})` })
                    },
                    async (payload) => {
                        const newChatRoom = payload.new;
                        handleOnChatRoomChange(newChatRoom);
                    }
                )
                .on(
                    'postgres_changes',
                    {
                        event: 'UPDATE',
                        schema: 'public',
                        table: 'chat_rooms',
                        ...(receiver !== 'seller' && { filter: `seller_id=in.(${sellerId})` })
                    },
                    async (payload) => {
                        const updatedChatRoom = payload.new;
                        handleOnChatRoomChange(updatedChatRoom);
                    }
                )
                .subscribe();
            subscribedChannels.push(chatRoomSubscription);

            if (chat.chatRooms.length !== 0) {
                const roomIds = chat.chatRooms.map((e) => e.id);
                const messageSubscription = supabase
                    .channel('public:messages')
                    .on(
                        'postgres_changes',
                        {
                            event: 'INSERT',
                            schema: 'public',
                            table: 'messages',
                            filter: `room_id=in.(${roomIds.join(',')})`
                        },
                        (payload) => {
                            handleOnGetNewMessage(payload.new.room_id, payload.new);
                        }
                    )
                    .subscribe();
                subscribedChannels.push(messageSubscription);
            }

            setSubscriptions(subscribedChannels);
        }
    }, [sellerId, chat.chatRooms]);

    const fetchMessages = async (roomId, page = 0, pageSize = 10) => {
        try {
            const chatRooms = [...chat.chatRooms];
            const chatRoom = chatRooms.find((e) => e.id === roomId);
            const appendedMessagesCount = chatRoom ? chatRoom.messages.length : 0;

            const {
                data: newMessages,
                error,
                count
            } = await supabase
                .from("messages")
                .select("*", { count: "exact" })
                .eq("room_id", roomId)
                .order("created_at", { ascending: false })
                .range(appendedMessagesCount, appendedMessagesCount + pageSize - 1);

            if (error) {
                throw new Error(`Messages fetching error: ${error}`);
            }

            const hasMore = count > appendedMessagesCount + newMessages.length;

            return {
                messages: newMessages,
                pagination: {
                    hasMore,
                    currentPage: page,
                    totalMessages: count
                }
            };
        } catch (error) {
            console.error(`Error loading messages for room ${roomId}:`, error);
            throw error;
        }
    };

    const loadMoreMessages = async (roomId) => {
        chat.setIsLoadingMore(true);

        try {
            const chatRooms = [...chat.chatRooms];
            const roomIndex = chatRooms.findIndex((r) => r.id === roomId);

            if (roomIndex === -1) {
                throw new Error(`Room with id ${roomId} not found`);
            }
            const { messages, pagination } = await fetchMessages(
                roomId,
                chatRooms[roomIndex].currentPage + 1
            );
            chat.appendMessages("start", roomId, messages, pagination);
        } catch (error) {
            console.error("Error loading more messages:", error);
        } finally {
            chat.setIsLoadingMore(false);
        }
    };

    const subscribe = async () => {
        try {
            chat.setIsLoading(true);
            // Query builder
            let supabaseQuery = supabase.from('chat_rooms').select('*');
            if (role !== "ROLE_ADMIN") {
                supabaseQuery = supabaseQuery.or(`seller_id.eq.${sellerId}`);
            }
            supabaseQuery = supabaseQuery.order('id', { ascending: false });

            const { data: chatRooms, error } = await supabaseQuery;

            if (error) {
                throw new Error(`ChatRooms fetching error: ${error}`);
            }

            const chatRoomsWithMessages = await Promise.all(
                chatRooms.map(async (room) => {
                    try {
                        const { messages, pagination } = await fetchMessages(room.id);
                        return {
                            ...room,
                            ...pagination,
                            messages
                        };
                    } catch (error) {
                        console.error(`Failed to fetch messages for room ${room.id}:`, error);
                        return {
                            ...room,
                            messages: [],
                            hasMore: false,
                            currentPage: 0,
                            totalMessages: 0,
                            error: error.message
                        };
                    }
                })
            );

            const chatRoomsWithData = await chat.loadChatRoomData(chatRoomsWithMessages);
            chat.setChatRooms(chatRoomsWithData);
        } catch (error) {
            console.log('Error occurred:', error);
        } finally {
            chat.setIsLoading(false);
        }
    };

    const unSubscribe = () => {
        if (subscriptions.length !== 0) {
            subscriptions.forEach((subscription) => supabase.removeChannel(subscription));
        }
    };

    return {
        ...chat,
        subscribe,
        unSubscribe,
        loadMoreMessages
    };
};

export default ChatProvider;
